import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-83d02916"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "label text-secondary" }
const _hoisted_2 = ["type", "name", "value", "disabled"]
const _hoisted_3 = {
  key: 1,
  class: /*#__PURE__*/_normalizeClass(['error'])
}

import { omit } from 'lodash';
import { computed, onMounted, ref, useAttrs } from 'vue';

import useBreakpoints from '@/composables/useBreakpoints';
import { Rules } from '@/types';
import useInputEvents from './composables/useInputEvents';
import useInputStyles from './composables/useInputStyles';
import useInputValidation from './composables/useInputValidation';

import { InputSize, InputType, InputValue, ValidationTrigger } from './types';

type Props = {
  name: string;
  modelValue: InputValue;
  isValid?: boolean;
  type?: InputType;
  size?: InputSize;
  disabled?: boolean;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  label?: string;
  inputAlignRight?: boolean;
  decimalLimit?: number;
  validateOn?: ValidationTrigger;
  rules?: Rules;
  noRadius?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  noPadding?: boolean;
  autoFocus?: boolean;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  format?: (input: string | number) => string | number;
  inputMaxWidth?: string;
  inputGroupMobileWrap?: boolean;
};

/**
 * PROPS & EMITS
 */

// https://v3.vuejs.org/api/sfc-script-setup.html#usage-alongside-normal-script
const __default__ = {
  inheritAttrs: false,
};

function setup(__props: any, { emit }: { emit: ({
  (e: 'blur', value: string): void;
  (e: 'input', value: string): void;
  (e: 'update:modelValue', value: string): void;
  (e: 'update:isValid', value: boolean): void;
  (e: 'keydown', value: KeyboardEvent);
  (e: 'mouseOver', value: Event);
  (e: 'mouseLeave', value: Event);
}), expose: any, slots: any, attrs: any }) {

const props = __props as { name: string, modelValue: InputValue, isValid: boolean, type: InputType, size: InputSize, disabled: boolean, label?: string, inputAlignRight: boolean, decimalLimit: number, validateOn: ValidationTrigger, rules: Rules, noRadius: boolean, noShadow: boolean, noBorder: boolean, noPadding: boolean, autoFocus: boolean, format?: (input: string | number) => string | number, inputMaxWidth: string, inputGroupMobileWrap: boolean }





/**
 * STATE
 */
const textInput = ref<HTMLInputElement>();

/**
 * COMPOSABLES
 */
const attrs = useAttrs();
const { errors, isInvalid, validate } = useInputValidation(props, emit);
const {
  isActive,
  isHover,
  onInput,
  onKeydown,
  onBlur,
  onClick,
  onFocus,
  onMouseOver,
  onMouseLeave,
} = useInputEvents(props, emit, validate);
const {
  parentClasses,
  inputContainerClasses,
  inputGroupClasses,
  headerClasses,
  footerClasses,
  inputClasses,
  prependClasses,
  appendClasses,
  borderRadiusClasses,
} = useInputStyles(props, isInvalid, isActive, isHover, attrs);

const { isMobile } = useBreakpoints();

/**
 * COMPUTED
 */
// We don't want to pass on parent level classes to the html
// input element. So we need to remove it from the attrs object.
const inputAttrs = computed(() => omit(attrs, 'class'));

/**
 * LIFECYCLE
 */
onMounted(() => {
  textInput.value?.focus();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(['bal-text-input', _unref(parentClasses), _unref(borderRadiusClasses)])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(['input-container', _unref(inputContainerClasses), _unref(borderRadiusClasses)]),
        onMouseover: _cache[5] || (_cache[5] = 
//@ts-ignore
(...args) => (_unref(onMouseOver) && _unref(onMouseOver)(...args))),
        onMouseleave: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(onMouseLeave) && _unref(onMouseLeave)(...args)))
      }, [
        (_ctx.$slots.header || __props.label)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(['header', _unref(headerClasses)])
            }, [
              _renderSlot(_ctx.$slots, "header", {}, () => [
                _createElementVNode("span", _hoisted_1, _toDisplayString(__props.label), 1)
              ])
            ], 2))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: _normalizeClass(['input-group', _unref(inputGroupClasses)]),
          style: _normalizeStyle(
          __props.inputGroupMobileWrap && _unref(isMobile) ? { flexDirection: 'column' } : {}
        )
        }, [
          (_ctx.$slots.prepend)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(['prepend', _unref(prependClasses)])
              }, [
                _renderSlot(_ctx.$slots, "prepend")
              ], 2))
            : _createCommentVNode("", true),
          _createElementVNode("input", _mergeProps({
            ref: (_value, _refs) => {
              _refs['textInput'] = _value
              textInput.value = _value
            },
            type: __props.type,
            name: __props.name,
            value: __props.modelValue
          }, _unref(inputAttrs), {
            disabled: __props.disabled,
            class: ['input', _unref(inputClasses)],
            style: 
            __props.inputMaxWidth
              ? {
                  width: __props.inputMaxWidth + 'px',
                  paddingRight: '0',
                  textAlign: 'left',
                }
              : {}
          ,
            onBlur: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onBlur) && _unref(onBlur)(...args))),
            onInput: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onInput) && _unref(onInput)(...args))),
            onKeydown: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(onKeydown) && _unref(onKeydown)(...args))),
            onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(onClick) && _unref(onClick)(...args))),
            onFocus: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(onFocus) && _unref(onFocus)(...args)))
          }), null, 16, _hoisted_2),
          (_ctx.$slots.max)
            ? _renderSlot(_ctx.$slots, "max", { key: 1 })
            : _createCommentVNode("", true),
          (_ctx.$slots.append)
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                class: _normalizeClass(['append', _unref(appendClasses)])
              }, [
                _renderSlot(_ctx.$slots, "append")
              ], 2))
            : _createCommentVNode("", true)
        ], 6),
        (_unref(isInvalid) && !!_unref(errors)[0])
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(errors)[0]), 1))
          : _createCommentVNode("", true)
      ], 34)
    ], 2),
    (_ctx.$slots.footer)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(['footer', _unref(footerClasses)])
        }, [
          _renderSlot(_ctx.$slots, "footer")
        ], 2))
      : _createCommentVNode("", true)
  ], 64))
}
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    name: null,
    modelValue: { default: '' },
    isValid: { type: Boolean, default: true },
    type: { default: 'text' },
    size: { default: 'md' },
    disabled: { type: Boolean, default: false },
    label: null,
    inputAlignRight: { type: Boolean, default: false },
    decimalLimit: { default: 18 },
    validateOn: { default: 'blur' },
    rules: { default: () => [] },
    noRadius: { type: Boolean, default: false },
    noShadow: { type: Boolean, default: false },
    noBorder: { type: Boolean, default: false },
    noPadding: { type: Boolean, default: false },
    autoFocus: { type: Boolean, default: false },
    format: null,
    inputMaxWidth: { default: '' },
    inputGroupMobileWrap: { type: Boolean, default: false }
  },
  emits: ["blur", "input", "update:modelValue", "update:isValid", "keydown", "mouseOver", "mouseLeave"],
  setup})